import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import dayjs from 'dayjs';
import { useState } from 'react';

import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../api/vault-api.ts';
import { timezones } from '../../../../../utils/timezones.ts';
import VaultMatterHeader from './shared/vault-matter-header.tsx';
import DecomNotifyUsersInput from './shared/decom-notify-users-input.tsx';
import DecomReportRecipientInput from './shared/decom-report-recipient-input.tsx';
import VaultCalendarAdvancedOptions from './shared/vault-calendar-advanced-options.tsx';
import VaultTooltipWithLink from './shared/vault-tooltip-with-link.tsx';
import VaultExportConfigSkeleton from './shared/vault-export-config-skeleton.tsx';
import VaultDatePicker from './shared/vault-date-picker.tsx';
import { DestUser, VAULT_DATA_SCOPE_OPTIONS } from '../../../../../../types.ts';

import { ActionErrorEntry } from '../decom-policy-utils.tsx';

import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export interface VaultCalendarExportConfigProps {
  matter?: VaultMatterModel;
  isLoading?: boolean;
  day: number;
  idx: number;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
}

export default function VaultCalendarExportConfigOld({
  matter,
  isLoading,
  day,
  idx,
  watch,
  setValue,
  control,
  errors,
  clearErrors,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
}: VaultCalendarExportConfigProps) {
  const vaultStartTime = watch(`actions.${day}.actions.${idx}.vaultStartTime`);
  const vaultEndTime = watch(`actions.${day}.actions.${idx}.vaultEndTime`);
  const vaultVersionDate = watch(
    `actions.${day}.actions.${idx}.vaultVersionDate`
  );

  const defaultDateRangeType =
    vaultStartTime || vaultEndTime || vaultVersionDate ? 'custom' : 'all';
  const [dateRangeType, setDateRangeType] =
    useState<string>(defaultDateRangeType);

  const handleBack = () => {
    setValue(`actions.${day}.actions.${idx}.matterId`, '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleDateRangeTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = e.target.value;
    setDateRangeType(val);
    if (val === 'all') {
      setValue(`actions.${day}.actions.${idx}.vaultStartTime`, null, {
        shouldDirty: true,
      });
      setValue(`actions.${day}.actions.${idx}.vaultEndTime`, null, {
        shouldDirty: true,
      });
      setValue(`actions.${day}.actions.${idx}.vaultVersionDate`, null, {
        shouldDirty: true,
      });
    }
  };

  if (isLoading) return <VaultExportConfigSkeleton onBack={handleBack} />;

  if (!matter) return null;

  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      <VaultMatterHeader matterName={matter.name} onBack={handleBack} />

      <Stack direction="row" gap={4}>
        <Controller
          name={`actions.${day}.actions.${idx}.dataScope`}
          control={control}
          defaultValue="ALL_DATA"
          render={({ field }) => (
            <FormControl sx={{ minWidth: 350 }} size="small">
              <Typography variant="subtitle2" gutterBottom>
                Source
              </Typography>
              <Select {...field} value={field.value || ''}>
                {VAULT_DATA_SCOPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Stack>

      <Stack direction="row" gap={4}>
        <Controller
          name={`actions.${day}.actions.${idx}.timeZone`}
          control={control}
          render={({ field }) => (
            <FormControl sx={{ minWidth: 350 }} size="small">
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="subtitle2" gutterBottom>
                  Timezone
                </Typography>
                <VaultTooltipWithLink
                  preset="vaultTimezone"
                  sxIcon={{ mb: 0.5 }}
                />
              </Stack>
              <Select {...field} value={field.value || ''} displayEmpty>
                {timezones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Stack>

      <Box>
        <RadioGroup value={dateRangeType} onChange={handleDateRangeTypeChange}>
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">All events up to now</Typography>
            }
          />
          <FormControlLabel
            value="custom"
            control={<Radio size="small" />}
            label={<Typography variant="body2">Custom Date Range</Typography>}
          />
        </RadioGroup>
      </Box>

      {dateRangeType === 'custom' && (
        <Stack direction="row" gap={4} flexWrap="wrap">
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2" gutterBottom>
                Event Date (optional)
              </Typography>
              <Tooltip
                title="Only events within this date range will be exported."
                arrow
              >
                <InfoOutlined
                  fontSize="small"
                  color="action"
                  sx={{ mb: 0.5 }}
                />
              </Tooltip>
            </Stack>
            <Stack direction="row" gap={2}>
              <VaultDatePicker
                day={day}
                idx={idx}
                name={`actions.${day}.actions.${idx}.vaultStartTime`}
                label="Start Date"
                control={control}
                errors={errors}
                maxDate={
                  vaultEndTime && dayjs(vaultEndTime).isValid()
                    ? dayjs(vaultEndTime)
                    : undefined
                }
              />
              <VaultDatePicker
                day={day}
                idx={idx}
                name={`actions.${day}.actions.${idx}.vaultEndTime`}
                label="End Date"
                control={control}
                errors={errors}
                minDate={
                  vaultStartTime && dayjs(vaultStartTime).isValid()
                    ? dayjs(vaultStartTime)
                    : undefined
                }
              />
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2" gutterBottom>
                Version Date (optional)
              </Typography>
              <VaultTooltipWithLink
                preset="calendarEventVersioning"
                sxIcon={{ mb: 0.5 }}
              />
            </Stack>
            <VaultDatePicker
              day={day}
              idx={idx}
              name={`actions.${day}.actions.${idx}.vaultVersionDate`}
              label="Version Date"
              control={control}
              errors={errors}
            />
          </Box>
        </Stack>
      )}

      <Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle2" gutterBottom>
            Terms (optional)
          </Typography>
          <VaultTooltipWithLink
            preset="calendarQueryTerms"
            sxIcon={{ mb: 0.5 }}
          />
        </Stack>
        <Controller
          name={`actions.${day}.actions.${idx}.terms`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value || ''}
              placeholder="Keywords contained in the title, description, organizers, guests, locations or rooms"
              helperText="Enter words or phrases in quotation marks"
              size="small"
              fullWidth
              variant="standard"
            />
          )}
        />
      </Box>

      <VaultCalendarAdvancedOptions day={day} idx={idx} control={control} />

      <Divider sx={{ my: 1 }} />

      <DecomNotifyUsersInput
        day={day}
        idx={idx}
        control={control}
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
      />

      <DecomReportRecipientInput
        day={day}
        idx={idx}
        control={control}
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        actionType="vaultCalendarExport"
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={destUserFields}
        destUserAppend={destUserAppend}
        destUserRemove={destUserRemove}
        destUserUpdate={destUserUpdate}
      />
    </Stack>
  );
}
